import React, { useState } from 'react';
import { theme } from '../theme';
import { BentoCard, SkeletonBlock } from './shared';
import {
  listVocabulary, saveVocabularyLabel, resetVocabularyLabel,
  VocabularyAxisEntry, VocabularyValueEntry,
} from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { useRevisionedDrafts } from '../hooks/useRevisionedDrafts';
import { btnStyle, feedbackStyle, pillStyle, InlineErrorZone, staleConflictNotice } from './overlayEditorChrome';

// <purpose>The vocabulary SECTION of the "Texte & Vokabular" tab: per-value editing of the
// classification wordings this tenant's own UI and workbooks show, with a revision trail and a
// reset that hands the value back to the layer beneath. Drives the vocabulary overlay rail
// (GET/POST /manage/vocabulary, DELETE /manage/vocabulary/{axis}/{valueId}) — the same
// baseRevision/409 contract the reply-prose section rides, on the same engine.</purpose>
// <dependencies>services/api (listVocabulary/saveVocabularyLabel/resetVocabularyLabel);
// useRevisionedDrafts (THE shared overlay-editing engine — load-merge, frozen baseRevision, 409
// rebase, per-row errors); overlayEditorChrome (the tab's one visual dialect); BentoCard +
// SkeletonBlock (shared.tsx).</dependencies>
// <constraints>The rail can NEVER add or remove a value — the catalog is driven by the tenant's
// SEED vocabulary, one row per declared value, and a save for anything else is refused by the
// backend. So this screen edits WORDINGS only; there is deliberately no add/delete affordance to
// suggest otherwise.
// A reset is not a restore: it drops this tenant's admin row so whichever layer sits beneath
// re-surfaces. The backend already computes what that would be (revertsTo), so the confirmation
// NAMES the wording that takes over rather than promising an unspecified "Standard" — a reset
// whose result the admin cannot see beforehand is the one they will not press.
// Loading posture (standing FE rule): skeleton shimmer in PRE-ALLOCATED space, never placeholder
// text and never a collapsed card that expands when data lands. ⚠ The skeleton gates on
// `initialLoading`, NOT on `loading` — every write triggers a refetch, and gating the rows on
// `loading` unmounted the whole catalog mid-write (height changed twice per save, and the 409
// path unmounted the rejected draft at the exact moment the notice asked the admin to compare
// it). Rows, once on screen, stay on screen; a refetch shows an inline hint in its own reserved
// slot instead. This also matches the prose section, which has always rendered its loading hint
// beside mounted rows. Rows are keyed `${axis}::${valueId}` because a value id is only unique
// within its axis.</constraints>

const labelInputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.colors.bgLight}`,
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

const axisHeaderStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
  letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
  borderBottom: `1px solid ${theme.colors.bgLight}`,
};

// The engine keys every map by one string; a value id repeats across axes, so the axis is part of
// the identity. Exported for the tests that assert per-row targeting.
export function rowKeyFor(axis: string, valueId: string): string {
  return `${axis}::${valueId}`;
}

// One catalog row, flattened out of the axis grouping so the engine sees a plain list. The axis is
// carried along because every write needs it.
interface FlatValueRow extends VocabularyValueEntry {
  axis: string;
  axisNoun: string;
}

function flatten(axes: VocabularyAxisEntry[]): FlatValueRow[] {
  return axes.flatMap(axis =>
    axis.values.map(value => ({ ...value, axis: axis.axis, axisNoun: axis.noun })));
}

const VokabularVerwaltung: React.FC = () => {
  const {
    rows, drafts, loading, initialLoading, error, notice, fieldErrors,
    setNotice, load, setDraftValue, baseRevisionFor, clearFreeze, rebaseFreeze,
    setFieldError, clearFieldError, isDirty,
  } = useRevisionedDrafts<FlatValueRow>(
    async () => flatten((await listVocabulary()).axes),
    row => ({
      key: rowKeyFor(row.axis, row.valueId),
      effectiveText: row.effectiveLabel,
      latestRevision: row.adminRevision,
    }),
    'Laden des Vokabulars',
  );

  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [resetConfirmKey, setResetConfirmKey] = useState<string | null>(null);
  const [resettingKey, setResettingKey] = useState('');

  const save = async (row: FlatValueRow) => {
    const key = rowKeyFor(row.axis, row.valueId);
    setSavingKey(key); setNotice(''); setSavedKey('');
    clearFieldError(key);
    const baseRevision = baseRevisionFor(row);
    try {
      await saveVocabularyLabel(row.axis, row.valueId, drafts[key] ?? '', baseRevision);
      clearFreeze(key);
      setSavedKey(key);
      await load();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setNotice(staleConflictNotice('Die Bezeichnung'));
        // User-acknowledged rebase — the one place the freeze moves forward without a fresh edit.
        rebaseFreeze(await load(), key);
      } else {
        setFieldError(key, formatBackendError(e, 'Speichern der Bezeichnung'));
      }
    } finally {
      setSavingKey('');
    }
  };

  const resetToInherited = async (row: FlatValueRow) => {
    const key = rowKeyFor(row.axis, row.valueId);
    setResettingKey(key); setNotice('');
    clearFieldError(key);
    try {
      await resetVocabularyLabel(row.axis, row.valueId);
      clearFreeze(key);
      setResetConfirmKey(null);
      await load();
    } catch (e) {
      setFieldError(key, formatBackendError(e, 'Zurücksetzen der Bezeichnung'));
    } finally {
      setResettingKey('');
    }
  };

  // Axis grouping is rebuilt from the flat rows rather than kept as a second copy of the catalog:
  // one source, so a row can never render under an axis it does not belong to.
  const axisOrder: string[] = [];
  const byAxis = new Map<string, FlatValueRow[]>();
  for (const row of rows) {
    if (!byAxis.has(row.axis)) { byAxis.set(row.axis, []); axisOrder.push(row.axis); }
    byAxis.get(row.axis)!.push(row);
  }

  return (
    <BentoCard title="Vokabular">
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        <p style={{ fontSize: 12, color: theme.colors.textSecondary, margin: 0 }}>
          Bezeichnungen der Klassifizierungswerte, wie sie in dieser Oberfläche und in Ihren
          Auswertungen erscheinen. Änderungen wirken sofort — ohne Deployment. Die Werte selbst
          bleiben unverändert; nur ihre Bezeichnung wird angepasst.
        </p>

        {error && <div style={feedbackStyle(true)}>{error}</div>}
        {notice && <div style={feedbackStyle(false)}>{notice}</div>}

        {/* Refresh hint for a write-triggered refetch, in a PRE-ALLOCATED slot that is always
            present — the rows below stay mounted throughout (see initialLoading), so this line
            appearing and clearing is the only thing that changes, and its box never collapses. */}
        <div style={{ minHeight: 16 }}>
          {loading && !initialLoading && (
            <span style={{ fontSize: 11, color: theme.colors.textSecondary }}>Wird aktualisiert…</span>
          )}
        </div>

        {/* Loading posture: shimmer in pre-allocated space, FIRST load only. Three placeholder rows
            carry the same footprint a real row has (label line, input, action row, reserved error
            zone). Never placeholder TEXT, and never a stand-in for rows that are already on screen
            — see the initialLoading contract in useRevisionedDrafts. */}
        {initialLoading && (
          <div data-testid="vokabular-skeleton" aria-hidden="true"
            style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6,
                borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
                <SkeletonBlock width={180} height={15} borderRadius={6} />
                <div style={{ minHeight: 18 }} />
                <SkeletonBlock width="100%" height={35} borderRadius={6} />
                <div style={{ minHeight: 32 }} />
                <div style={{ minHeight: 48 }} />
              </div>
            ))}
          </div>
        )}

        {!initialLoading && axisOrder.map(axis => {
          const axisRows = byAxis.get(axis)!;
          return (
            <div key={axis}>
              <div style={axisHeaderStyle}>{axisRows[0].axisNoun}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                {axisRows.map(row => {
                  const key = rowKeyFor(row.axis, row.valueId);
                  const dirty = isDirty(row);
                  const hasOwnWording = row.source === 'admin';

                  return (
                    <div key={key} data-testid={`vocab-row-${key}`}
                      style={{ display: 'flex', flexDirection: 'column', gap: 6,
                        borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
                      <label htmlFor={`vocab-${key}`} style={{ fontSize: 13, fontWeight: 600, color: theme.colors.navyMid }}>
                        {row.seedLabel}
                        <span style={{ fontWeight: 400, color: theme.colors.textSecondary, marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>
                          {row.valueId}
                        </span>
                      </label>

                      {/* Pre-allocated badge row — same idiom and same "only when it says something"
                          rule as the reply-prose section: no pill for a value still on its seed
                          wording (that is every value's default and conveys nothing). */}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', minHeight: 18 }}>
                        {hasOwnWording ? (
                          <span style={pillStyle('override')}>{`Angepasst · Rev ${row.adminRevision}`}</span>
                        ) : row.source === 'institutional' ? (
                          <span style={pillStyle('frozen')}>Vorgabe</span>
                        ) : null}
                      </div>

                      <input
                        id={`vocab-${key}`}
                        type="text"
                        style={labelInputStyle}
                        value={drafts[key] ?? ''}
                        onChange={e => setDraftValue(row, e.target.value)}
                      />

                      {/* Pre-allocated action row — buttons swap in and out inside it, the row's own
                          footprint never changes. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap', minHeight: 32 }}>
                        <button onClick={() => save(row)} style={btnStyle('primary')}
                          disabled={savingKey === key || !dirty}>
                          {savingKey === key ? 'Speichern…' : 'Speichern'}
                        </button>

                        {dirty && (
                          <button onClick={() => setDraftValue(row, row.effectiveLabel)} style={btnStyle('ghost')}>
                            Zurücksetzen
                          </button>
                        )}

                        {hasOwnWording && (
                          resetConfirmKey === key ? (
                            <>
                              {/* The confirmation NAMES what takes over (revertsTo), because the
                                  backend already knows it — see <constraints>. */}
                              <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                                {`Eigene Bezeichnung verwerfen und „${row.revertsTo}“ verwenden?`}
                              </span>
                              <button onClick={() => resetToInherited(row)} style={btnStyle('danger')}
                                disabled={resettingKey === key}>
                                {resettingKey === key ? 'Setzt zurück…' : 'Bestätigen'}
                              </button>
                              <button onClick={() => setResetConfirmKey(null)} style={btnStyle('ghost')}>
                                Abbrechen
                              </button>
                            </>
                          ) : (
                            <button onClick={() => setResetConfirmKey(key)} style={btnStyle('ghost')}>
                              Eigene Bezeichnung entfernen
                            </button>
                          )
                        )}

                        {savedKey === key && !dirty && (
                          <span style={{ fontSize: 12, color: '#1f7a3d' }}>gespeichert</span>
                        )}
                      </div>

                      <InlineErrorZone rowKey={key} message={fieldErrors[key]} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {!initialLoading && rows.length === 0 && (
          <div style={{ color: theme.colors.textSecondary, fontSize: 13 }}>
            Kein Vokabular hinterlegt.
          </div>
        )}
      </div>
    </BentoCard>
  );
};

export default VokabularVerwaltung;
